import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, Bath, Sparkles, UtensilsCrossed, Waves, PawPrint, Dog, Sunset, BedDouble, Users } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { IMG, COTTAGES } from "@/data/content";

const FILTERS = [
  { key: "all", label: "All cottages" },
  { key: "2", label: "Sleeps 2" },
  { key: "4", label: "Sleeps 4" },
  { key: "6", label: "Sleeps 6" },
];

const FACILITY_ICONS = [
  { icon: BedDouble, text: "20 bedrooms across cottages & farmhouse" },
  { icon: Users, text: "Sleeps up to 40 guests" },
  { icon: Flame, text: "Log fires throughout" },
  { icon: Bath, text: "Hot tubs under dark skies" },
  { icon: Sparkles, text: "Rustic spa rituals" },
  { icon: UtensilsCrossed, text: "Outdoor feasting & fire pits" },
  { icon: Waves, text: "Wild swimming pond" },
  { icon: PawPrint, text: "Resident goats — the FERAL Mowers" },
  { icon: Dog, text: "Dog friendly, surrounded by dog fields" },
  { icon: Sunset, text: "Beautiful sunset views" },
];

export default function Estate() {
  const [filter, setFilter] = useState("all");
  const shown = filter === "all" ? COTTAGES : COTTAGES.filter((c) => String(c.sleeps) === filter);

  return (
    <main data-testid="estate-page">
      <section className="relative flex min-h-[70vh] items-end overflow-hidden bg-[#1E1610]">
        <img src={IMG.estate} alt="The grounds of Plas Llechylched" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1610] via-[#1E1610]/50 to-[#1E1610]/20" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#1E1610]/70 to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-14 pt-40 sm:px-8 lg:px-16">
          <Reveal>
            <Eyebrow light>The estate</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#F4F1EA] sm:text-5xl lg:text-6xl">
              Eight luxury cottages around one stone courtyard.
            </h1>
            <p className="mt-5 max-w-xl text-[#F4F1EA]/75">
              Self-contained hideaways with their own front doors, log fires and slow mornings — plus the farmhouse,
              the communal heart of it all.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section data-testid="cottage-explorer" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <Reveal className="flex flex-wrap items-center justify-between gap-6">
          <h2 className="font-serif text-3xl tracking-tight text-[#241A12] sm:text-4xl">Find your cottage</h2>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`cottage-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
                  filter === f.key
                    ? "bg-[#5A4632] text-[#F4F1EA]"
                    : "border border-[#5A4632]/25 text-[#5A4632] hover:border-[#B85D43] hover:text-[#B85D43]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {shown.map((c, i) => (
            <Reveal key={c.name} delay={(i % 4) * 0.08}>
              <article data-testid={`cottage-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group">
                <div className="overflow-hidden rounded-2xl">
                  <img src={c.img} alt={`${c.name} cottage`} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-3">
                  <h3 className="font-serif text-2xl text-[#241A12]">{c.name}</h3>
                  <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.15em] text-[#B85D43]">Sleeps {c.sleeps}</span>
                </div>
                <p className="mt-0.5 font-serif italic text-[#7A6A58]">{c.meaning}</p>
                <p className="mt-2 text-sm leading-relaxed text-[#7A6A58]">{c.blurb}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14 text-center">
          <Link
            to="/enquire"
            data-testid="estate-cta-enquire"
            className="group inline-flex items-center gap-3 rounded-full bg-[#B85D43] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F4F1EA] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#5A4632]"
          >
            Check availability
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <section data-testid="estate-facilities" className="bg-[#1E1610] py-20 text-[#F4F1EA] lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal>
            <Eyebrow light>On the estate</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-[1.15] tracking-tight sm:text-4xl">
              Everything for slow, communal living.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {FACILITY_ICONS.map(({ icon: Icon, text }, i) => (
              <Reveal key={text} delay={(i % 3) * 0.08}>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2B2015] text-[#C89D56]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <p className="pt-2.5 text-sm text-[#F4F1EA]/80">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="estate-farmhouse" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Eyebrow>The farmhouse</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight text-[#241A12] sm:text-4xl">
              The communal heart of the estate.
            </h2>
            <p className="mt-6 leading-relaxed text-[#7A6A58]">
              A big kitchen built for feasting, gathering lounges with log fires and long dining tables that seat the
              whole party. Sleeps up to 14 on its own — or take the whole estate for 40.
            </p>
            <Link
              to="/enquire"
              data-testid="farmhouse-cta-enquire"
              className="group mt-8 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[#B85D43] transition-colors hover:text-[#5A4632]"
            >
              Enquire about the farmhouse
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG.farmhouse} alt="The farmhouse at Plas Llechylched" className="mt-8 aspect-[3/4] w-full rounded-2xl object-cover" />
              <img src={IMG.farmhouse2} alt="Shared spaces inside the farmhouse" className="aspect-[3/4] w-full rounded-2xl object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
